/**
 * `BuyerDetailPanel` — per-mandate buyer-detail review surface
 * (ADR-0065 §10). Mounts on the `MandateDetail` audit-record panel
 * between the existing intent summary and the request/response capture.
 *
 * Four states (per ADR §10):
 *
 *   - **State B (captured, owner/admin/reviewer):** disclosable card
 *     showing email, first_name + last_name, phone, shipping_address.
 *     `[copy]` affordances on email + phone.
 *
 *   - **State A (unavailable, 404 with a `reason` discriminant):**
 *     per-reason copy. Does NOT collapse the four reasons into one
 *     message — the merchant needs to distinguish "the mandate never
 *     carried buyer fields" from "the encrypter was misconfigured" so
 *     ops can act.
 *
 *   - **State C (purged, 200 with `purged_at`):** redacted card showing
 *     the purge timestamp + actor label.
 *
 *   - **State D (viewer role, hidden entirely):** the panel renders
 *     `BUYER · Hidden from your role` and skips the fetch. The server
 *     returns 403 for `viewer`; the panel never asks.
 *
 * The panel reads the session role from `useAuth()` and gates the
 * decrypt fetch + the purge action accordingly. The server enforces
 * the same gates (defense in depth) — the UI hides what the role
 * cannot perform.
 *
 * Owner + admin see a "Purge buyer details" affordance that opens a
 * `<ConfirmDialog />`; on success the panel transitions to State C
 * without a refetch (the API echoes the new `purged_at` + `purged_by`).
 *
 * Design tokens come from `useC()`. Mono uppercase kickers + serif
 * absent (the panel sits under MandateDetail's heading already); sans
 * body. Coral border on the purge button. No emoji. Copy is precise +
 * technical per CLAUDE.md §8.
 */

import { Check, Copy, Eye, ShieldX, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { AuditRecordId } from '@sill/types';
import type { UserRole } from '@sill/types';

import type { HttpClient } from '../../api/http-client.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { Card, ConfirmDialog } from '../../components/atoms/index.js';
import { useDisplayTimeZone } from '../../app/time-zone-context.js';
import { formatIsoCalendarDate, formatIsoDate } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import {
  fetchBuyerDetail,
  purgeBuyerDetail,
  type BuyerDetailCanonical,
  type BuyerDetailFetchResult,
  type BuyerDetailPurgedBy,
  type BuyerDetailUnavailableReason,
} from '../../api/audit-buyer-detail.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface BuyerDetailPanelProps {
  /**
   * The audit-record id the buyer detail belongs to. The panel calls
   * `GET /v1/audit/:record_id/buyer-detail` on mount + when this id
   * changes (switching to a different record in the list).
   */
  record_id: AuditRecordId;
  /**
   * Optional role override for tests that don't wire `<AuthProvider />`.
   * Production omits this and the panel reads role from `useAuth()`.
   */
  role_override?: UserRole;
  /**
   * Optional fetch override (ADR-0094 Slice 3). The escalation
   * review-queue reuses this panel's visual language to reveal the
   * buyer for a HELD pending escalation, but the data comes from
   * `GET /v1/escalations/:id/buyer-detail` rather than the audit
   * route. Passing a fetcher lets the escalation reveal drive the
   * same four states (captured / unavailable / forbidden / error)
   * without forking the renderer. The fetcher is re-invoked when
   * `record_id` changes, identical to the default audit fetch.
   *
   * The escalation reveal has no purge action; passing this override
   * also implies `hide_purge` so the owner/admin purge affordance is
   * not rendered for a not-yet-resolved order.
   */
  fetch_override?: (http: HttpClient, id: string) => Promise<BuyerDetailFetchResult>;
  /**
   * Suppress the owner/admin "Purge buyer detail" affordance. Set
   * implicitly by `fetch_override` consumers (the escalation reveal);
   * defaults to false for the audit-record panel.
   */
  hide_purge?: boolean;
  /**
   * Controls WHEN the buyer-detail decrypt fetch fires (ADR-0120 §4
   * security must-hold 3 — no auto-reveal of buyer detail from the
   * shadow surface). Two modes:
   *
   *   - `'auto'` (DEFAULT) — preserves the Mandates view behavior
   *     EXACTLY. For `can_decrypt` roles the fetch fires on mount /
   *     when `record_id` changes, the access-log entry is written,
   *     and the buyer-detail rate limit / bulk-exfil counter advances
   *     immediately. This is the pre-ADR-0120 behavior; callers in
   *     the Mandates view leave this prop unset.
   *
   *   - `'on_click'` — used by the shadow-log "View mandate" deep-link
   *     pivot (ADR-0120). No fetch on mount; the panel renders a
   *     "Reveal buyer detail" affordance for `can_decrypt` roles. The
   *     fetch (and therefore the access-log entry + counter advance)
   *     only fires after the operator explicitly clicks reveal. This
   *     keeps policy-tuning review from silently triggering a buyer
   *     decrypt + access-log write + bulk-exfil count.
   *
   * Viewer-role behavior (State D, no fetch ever) is unchanged in both
   * modes — the gate is purely additive on top of the existing
   * role-gate. The fetch function, the route, and the access-log
   * semantics are unchanged — only WHEN the fetch fires.
   */
  reveal?: 'auto' | 'on_click';
}

/**
 * Local UI state. Loading is its own state so the panel renders a
 * stable skeleton while the request is in flight; the
 * `BuyerDetailFetchResult` union from the API client carries the
 * resolved shape afterwards.
 */
type PanelState =
  | { kind: 'loading' }
  | { kind: 'resolved'; result: BuyerDetailFetchResult };

export function BuyerDetailPanel({
  record_id,
  role_override,
  fetch_override,
  hide_purge,
  reveal,
}: BuyerDetailPanelProps): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const toast = useToast();

  const role: UserRole | null = useMemo(() => {
    if (role_override !== undefined) return role_override;
    if (auth !== null && auth.state.kind === 'authenticated') {
      return auth.state.user.role;
    }
    return null;
  }, [role_override, auth]);

  const can_decrypt = role === 'owner' || role === 'admin' || role === 'reviewer';
  // The purge affordance is owner/admin only, and is suppressed entirely
  // when the panel is being driven by an injected fetcher (the
  // escalation reveal, ADR-0094 Slice 3 — a held order is not yet a
  // retained audit record, so there is nothing to right-to-erasure).
  const can_purge =
    (role === 'owner' || role === 'admin')
    && hide_purge !== true
    && fetch_override === undefined;

  // ADR-0120 §4 must-hold 3 — `'on_click'` mode defers the decrypt
  // fetch until the operator explicitly reveals. DEFAULT is `'auto'`,
  // which preserves the Mandates view behavior exactly (fetch on
  // mount for `can_decrypt` roles). Viewer-role behavior (State D)
  // is unchanged in both modes — see the role-gate above.
  const reveal_mode: 'auto' | 'on_click' = reveal ?? 'auto';
  const [revealed, setRevealed] = useState<boolean>(reveal_mode === 'auto');

  const [state, setState] = useState<PanelState>({ kind: 'loading' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [purging, setPurging] = useState(false);

  // Reset the reveal latch when the gate or the record changes. A
  // record-id switch under `'on_click'` mode must NOT auto-reveal the
  // new record's buyer — the operator's click was scoped to the prior
  // record. Under `'auto'` the latch stays open (preserves the
  // Mandates view's fetch-on-id-change behavior).
  useEffect(() => {
    setRevealed(reveal_mode === 'auto');
  }, [reveal_mode, record_id]);

  // Fetch on mount + when record_id changes. Skip the fetch entirely
  // for viewer-role sessions (State D) — the server returns 403 but
  // we'd be making an unnecessary call. Also skip when the HTTP
  // provider is absent (legacy test scaffolds) — the panel stays in
  // loading state but nothing crashes. Under `'on_click'` mode, also
  // skip until the operator has explicitly clicked Reveal — see
  // ADR-0120 §4 must-hold 3 (no auto-reveal of buyer detail from the
  // shadow surface). The fetch function, route, and access-log
  // semantics are unchanged — only WHEN the fetch fires.
  useEffect(() => {
    let cancelled = false;
    if (!can_decrypt) return;
    if (http === null) return;
    if (!revealed) return;
    setState({ kind: 'loading' });
    void (async () => {
      const result = fetch_override !== undefined
        ? await fetch_override(http, record_id)
        : await fetchBuyerDetail(http, record_id);
      if (cancelled) return;
      setState({ kind: 'resolved', result });
    })();
    return () => {
      cancelled = true;
    };
  }, [http, record_id, can_decrypt, fetch_override, revealed]);

  const onPurge = useCallback(async (): Promise<void> => {
    if (!can_purge) return;
    if (purging) return;
    if (http === null) return;
    setPurging(true);
    try {
      const result = await purgeBuyerDetail(http, record_id);
      switch (result.kind) {
        case 'purged': {
          // Transition the panel to State C without a refetch — the
          // server's response carries the just-stamped purged_at and
          // purged_by.
          setState({
            kind: 'resolved',
            result: {
              kind: 'purged',
              purged_at: result.purged_at,
              purged_by: result.purged_by,
            },
          });
          toast({
            tone: result.already_purged ? 'mint' : 'lime',
            title: result.already_purged
              ? 'Buyer detail was already purged.'
              : 'Buyer detail purged.',
          });
          break;
        }
        case 'forbidden':
          toast({
            tone: 'coral',
            title: 'Your role cannot purge buyer detail.',
          });
          break;
        case 'not_found':
          toast({
            tone: 'coral',
            title: 'Audit record not found.',
          });
          break;
        case 'error':
          toast({
            tone: 'coral',
            title: 'Purge failed.',
            body: result.message,
          });
          break;
      }
    } finally {
      setPurging(false);
    }
  }, [can_purge, http, purging, record_id, toast]);

  // State D — viewer role. Render a single-line role-hidden notice so
  // the merchant knows the section exists but is gated; do not fetch.
  if (!can_decrypt) {
    return <RoleHiddenPanel C={C} />;
  }

  // ADR-0120 §4 must-hold 3 — `'on_click'` mode renders the gated
  // reveal affordance for `can_decrypt` roles until the operator
  // explicitly clicks. No decrypt fetch + no access-log entry +
  // no rate-limit / bulk-exfil counter advance happen until reveal.
  if (!revealed) {
    return (
      <RevealGatePanel
        C={C}
        onReveal={() => setRevealed(true)}
      />
    );
  }

  if (state.kind === 'loading') {
    return <LoadingPanel C={C} />;
  }

  return (
    <PanelBody
      C={C}
      result={state.result}
      can_purge={can_purge}
      purging={purging}
      onOpenPurgeConfirm={() => setConfirmOpen(true)}
      confirmOpen={confirmOpen}
      onCloseConfirm={() => setConfirmOpen(false)}
      onConfirmPurge={() => {
        void onPurge();
      }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Sub-renderers — one per visual state.                                */
/* ------------------------------------------------------------------ */

function PanelBody({
  C,
  result,
  can_purge,
  purging,
  onOpenPurgeConfirm,
  confirmOpen,
  onCloseConfirm,
  onConfirmPurge,
}: {
  C: ReturnType<typeof useC>;
  result: BuyerDetailFetchResult;
  can_purge: boolean;
  purging: boolean;
  onOpenPurgeConfirm: () => void;
  confirmOpen: boolean;
  onCloseConfirm: () => void;
  onConfirmPurge: () => void;
}): JSX.Element {
  return (
    <section
      aria-label="Buyer detail"
      className="p-4 mb-5"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <Kicker C={C}>BUYER DETAIL</Kicker>
      {result.kind === 'captured' ? (
        <CapturedView
          C={C}
          buyer={result.buyer}
          can_purge={can_purge}
          purging={purging}
          onOpenPurgeConfirm={onOpenPurgeConfirm}
        />
      ) : null}
      {result.kind === 'purged' ? (
        <PurgedView
          C={C}
          purged_at={result.purged_at}
          {...(result.purged_by !== undefined
            ? { purged_by: result.purged_by }
            : {})}
        />
      ) : null}
      {result.kind === 'not_captured' ? (
        <UnavailableView C={C} reason={result.reason} />
      ) : null}
      {result.kind === 'forbidden' ? (
        <ForbiddenView C={C} />
      ) : null}
      {result.kind === 'error' ? (
        <ErrorView C={C} message={result.message} />
      ) : null}
      <ConfirmDialog
        open={confirmOpen}
        onClose={onCloseConfirm}
        onConfirm={onConfirmPurge}
        icon={Trash2}
        confirmTone="coral"
        title="Purge buyer detail"
        body={
          <>
            This NULLs the encrypted buyer block for this record and
            clears the correlating email-hash handle. The signed audit
            envelope remains intact. This cannot be undone.
          </>
        }
        confirmLabel={purging ? 'Purging…' : 'Purge buyer detail'}
      />
    </section>
  );
}

function CapturedView({
  C,
  buyer,
  can_purge,
  purging,
  onOpenPurgeConfirm,
}: {
  C: ReturnType<typeof useC>;
  buyer: BuyerDetailCanonical;
  can_purge: boolean;
  purging: boolean;
  onOpenPurgeConfirm: () => void;
}): JSX.Element {
  const full_name = [buyer.first_name, buyer.last_name]
    .filter((s): s is string => typeof s === 'string' && s.length > 0)
    .join(' ');
  const ship = buyer.shipping_address;
  // ADR-0067 — render only when present + non-empty. The map shape is
  // already validated upstream (≤ 16 entries, snake_case keys,
  // ≤ 256-char values, `sill_*` rejected); the panel renders verbatim.
  const custom_field_entries =
    buyer.custom_fields !== undefined
      ? Object.entries(buyer.custom_fields)
      : [];
  return (
    <div>
      <div className="mt-2 space-y-2">
        {typeof buyer.email === 'string' && buyer.email.length > 0 ? (
          <CopyableLine C={C} value={buyer.email} aria_label="Copy buyer email" />
        ) : null}
        {full_name.length > 0 ? (
          <PlainLine C={C}>{full_name}</PlainLine>
        ) : null}
        {typeof buyer.birthday === 'string' && buyer.birthday.length > 0 ? (
          <LabeledLine C={C} label="Date of birth">
            {formatIsoCalendarDate(buyer.birthday)}
          </LabeledLine>
        ) : null}
        {typeof buyer.phone === 'string' && buyer.phone.length > 0 ? (
          <CopyableLine C={C} value={buyer.phone} aria_label="Copy buyer phone" />
        ) : null}
        {typeof buyer.accepts_marketing === 'boolean' ? (
          <LabeledLine C={C} label="Marketing consent">
            {buyer.accepts_marketing ? 'subscribed' : 'not subscribed'}
          </LabeledLine>
        ) : null}
      </div>
      {ship !== undefined ? (
        <div className="mt-4">
          <Kicker C={C}>SHIPPING ADDRESS</Kicker>
          <div className="mt-2">
            {typeof ship.name === 'string' && ship.name.length > 0 ? (
              <PlainLine C={C}>{ship.name}</PlainLine>
            ) : null}
            {/* ADR-0074 — agent-signed company name. Render directly
                under the name when present + non-empty (PII-class field
                from the buyer-detail vault). */}
            {typeof ship.company === 'string' && ship.company.length > 0 ? (
              <PlainLine C={C}>{ship.company}</PlainLine>
            ) : null}
            <PlainLine C={C}>{ship.line1}</PlainLine>
            {typeof ship.line2 === 'string' && ship.line2.length > 0 ? (
              <PlainLine C={C}>{ship.line2}</PlainLine>
            ) : null}
            <PlainLine C={C}>
              {ship.city}, {ship.region} {ship.postal_code} {ship.country}
            </PlainLine>
          </div>
        </div>
      ) : null}
      {custom_field_entries.length > 0 ? (
        <div className="mt-4">
          <Kicker C={C}>CUSTOM FIELDS</Kicker>
          <CustomFieldsTable C={C} entries={custom_field_entries} />
        </div>
      ) : null}
      {can_purge ? (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onOpenPurgeConfirm}
            disabled={purging}
            aria-haspopup="dialog"
            className="flex items-center gap-2 px-3 py-1.5"
            style={{
              background: 'transparent',
              border: `1px solid ${C.coralFg}`,
              color: C.coralFg,
              borderRadius: 2,
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 2,
              cursor: purging ? 'not-allowed' : 'pointer',
              opacity: purging ? 0.6 : 1,
            }}
          >
            <Trash2 size={11} />
            {purging ? 'PURGING…' : 'PURGE BUYER DETAIL'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PurgedView({
  C,
  purged_at,
  purged_by,
}: {
  C: ReturnType<typeof useC>;
  purged_at: string;
  purged_by?: BuyerDetailPurgedBy;
}): JSX.Element {
  const timeZone = useDisplayTimeZone();
  const actor = describePurgedBy(purged_by);
  return (
    <div
      className="mt-2"
      style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}
    >
      Purged {formatIsoDate(purged_at, timeZone)}
      {actor !== null ? <> by {actor}</> : null} (right-to-erasure).
      The signed audit envelope is unchanged.
    </div>
  );
}

function UnavailableView({
  C,
  reason,
}: {
  C: ReturnType<typeof useC>;
  reason: BuyerDetailUnavailableReason;
}): JSX.Element {
  // Purged-reason 404 is treated by the server as a State-C row that
  // got squashed on the not-found branch; keep distinct copy so the
  // merchant still sees the "right-to-erasure" framing.
  if (reason === 'purged') {
    return (
      <div
        className="mt-2"
        style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}
      >
        Buyer details were purged per retention policy.
      </div>
    );
  }
  return (
    <div
      className="mt-2"
      style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}
    >
      {COPY_BY_REASON[reason]}
    </div>
  );
}

const COPY_BY_REASON: Record<
  Exclude<BuyerDetailUnavailableReason, 'purged'>,
  string
> = {
  buyer_absent_at_mandate:
    'No buyer details were attached to this mandate.',
  encrypter_unavailable:
    'Buyer detail capture was not configured when this transaction was processed.',
  payload_too_large:
    'Buyer details could not be stored — the payload exceeded the storage bound.',
};

function ForbiddenView({ C }: { C: ReturnType<typeof useC> }): JSX.Element {
  // Reached only if the server returned 403 despite the panel's
  // role-gate pre-check (e.g. role was demoted mid-session and the
  // route returned 403 on the next request). Render the same hidden
  // line as State D so the surface is consistent.
  return (
    <div
      className="mt-2 flex items-center gap-2"
      style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}
    >
      <ShieldX size={11} />
      Hidden from your role.
    </div>
  );
}

function ErrorView({
  C,
  message,
}: {
  C: ReturnType<typeof useC>;
  message: string;
}): JSX.Element {
  return (
    <div
      className="mt-2"
      style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5 }}
    >
      Couldn't load buyer detail — {message}
    </div>
  );
}

function LoadingPanel({ C }: { C: ReturnType<typeof useC> }): JSX.Element {
  return (
    <section
      aria-label="Buyer detail"
      aria-busy="true"
      className="p-4 mb-5"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <Kicker C={C}>BUYER DETAIL</Kicker>
      <div
        className="mt-2"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          letterSpacing: 1,
        }}
      >
        Loading…
      </div>
    </section>
  );
}

/**
 * Gated reveal affordance (ADR-0120 §4 must-hold 3). Renders when the
 * panel is in `'on_click'` mode and the operator has not yet clicked
 * Reveal. Voice + chrome match the panel's other states (mono kicker,
 * sans body, thin border, precise + technical copy — no exclamation).
 * Clicking fires the same decrypt fetch the `'auto'` mode would have
 * fired on mount; the access-log entry + the buyer-detail rate-limit /
 * bulk-exfil counter advance only on click.
 */
function RevealGatePanel({
  C,
  onReveal,
}: {
  C: ReturnType<typeof useC>;
  onReveal: () => void;
}): JSX.Element {
  return (
    <section
      aria-label="Buyer detail"
      className="p-4 mb-5"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <Kicker C={C}>BUYER DETAIL</Kicker>
      <div
        className="mt-2"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        Buyer detail is not auto-decrypted from this surface. Reveal logs a
        buyer-detail access entry against your account.
      </div>
      <div className="mt-3">
        <button
          type="button"
          onClick={onReveal}
          data-testid="buyer-detail-reveal"
          className="flex items-center gap-2 px-3 py-1.5"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.text,
            borderRadius: 2,
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 2,
            cursor: 'pointer',
          }}
        >
          <Eye size={11} />
          REVEAL BUYER DETAIL
        </button>
      </div>
    </section>
  );
}

function RoleHiddenPanel({ C }: { C: ReturnType<typeof useC> }): JSX.Element {
  return (
    <section
      aria-label="Buyer detail"
      className="p-3 mb-5 flex items-center gap-2"
      style={{
        background: C.surface2,
        border: `1px dashed ${C.borderSoft}`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 10,
        color: C.muted,
        letterSpacing: 2,
      }}
    >
      <ShieldX size={11} />
      BUYER · HIDDEN FROM YOUR ROLE
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Atoms — kicker / lines / copy button.                               */
/* ------------------------------------------------------------------ */

function Kicker({
  C,
  children,
}: {
  C: ReturnType<typeof useC>;
  children: string;
}): JSX.Element {
  return (
    <div
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        color: C.muted,
        letterSpacing: 2,
      }}
    >
      {children}
    </div>
  );
}

function PlainLine({
  C,
  children,
}: {
  C: ReturnType<typeof useC>;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        fontFamily: sansFont,
        fontSize: 13,
        color: C.text,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Label + value line for sub-fields that don't merit a copy button
 * (boolean consent, calendar-date birthday). The label uses a mono
 * uppercase kicker to match the existing identity-block sub-section
 * density; the value renders in sans body.
 */
function LabeledLine({
  C,
  label,
  children,
}: {
  C: ReturnType<typeof useC>;
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-baseline gap-2">
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          lineHeight: 1.5,
        }}
      >
        {children}
      </span>
    </div>
  );
}

/**
 * Tight two-column key/value table for ADR-0067 `custom_fields`. Keys in
 * mono `'JetBrains Mono'` (they ARE snake_case identifiers); values in
 * sans `'Geist'`. Thin 1px border, radius 2px, `C.surface` background to
 * sit one tier below the panel's `C.surface2`. NOT a real HTML `<table>`
 * — a CSS grid keeps key/value alignment crisp without table semantics
 * that would confuse the surrounding identity block.
 */
function CustomFieldsTable({
  C,
  entries,
}: {
  C: ReturnType<typeof useC>;
  entries: ReadonlyArray<readonly [string, string]>;
}): JSX.Element {
  return (
    <div
      className="mt-2"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      {entries.map(([key, value], idx) => (
        <div
          key={key}
          className="grid"
          style={{
            gridTemplateColumns: 'minmax(140px, max-content) 1fr',
            gap: 12,
            padding: '6px 10px',
            borderTop: idx === 0 ? 'none' : `1px solid ${C.borderSoft}`,
          }}
        >
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              lineHeight: 1.5,
              wordBreak: 'break-all',
            }}
          >
            {key}
          </span>
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              lineHeight: 1.5,
              wordBreak: 'break-word',
            }}
          >
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}

function CopyableLine({
  C,
  value,
  aria_label,
}: {
  C: ReturnType<typeof useC>;
  value: string;
  aria_label: string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(value);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="flex items-center gap-2">
      <span
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          lineHeight: 1.5,
          flex: 1,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={onCopy}
        aria-label={aria_label}
        className="flex items-center gap-1 px-1.5 py-0.5"
        style={{
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          fontFamily: monoFont,
          fontSize: 9,
          letterSpacing: 1,
          color: copied ? C.limeFg : C.muted,
          cursor: 'pointer',
        }}
      >
        {copied ? <Check size={9} /> : <Copy size={9} />}
        {copied ? 'COPIED' : 'COPY'}
      </button>
    </div>
  );
}

function describePurgedBy(p: BuyerDetailPurgedBy | undefined): string | null {
  switch (p) {
    case 'merchant_owner':
      return 'owner';
    case 'merchant_admin':
      return 'admin';
    case 'retention_cron':
      return 'retention cron';
    default:
      return null;
  }
}
